import { Stack } from '@src/shared/ui/layout';
import { type ReactNode, useLayoutEffect } from 'react';
import { PAGE_CONTENT_ROW_ID } from './constants';
import { usePageLayoutActions } from './hooks';
import Sidebar from './Sidebar';

type Props = {
  fillHeight: boolean;
  children: ReactNode;
};

/**
 * The flex row that holds the page body and the right `<Sidebar />`. It also
 * measures its own distance from the viewport top so `<Sidebar />` and floating
 * `SidePanel` overlays can size themselves to the visible viewport area instead
 * of growing past it (which is what hid the sidebar footer button on
 * non-`fillHeight` pages).
 */
export default function PageContentRow({ fillHeight, children }: Props) {
  const { setPageContentTop } = usePageLayoutActions();

  useLayoutEffect(() => {
    const row = document.getElementById(PAGE_CONTENT_ROW_ID);
    if (!row) {
      return;
    }

    let frame = 0;
    let last = -1;

    const measure = () => {
      frame = 0;
      const top = Math.max(Math.round(row.getBoundingClientRect().top), 0);
      if (top !== last) {
        last = top;
        setPageContentTop(top);
      }
    };

    const scheduleMeasure = () => {
      if (!frame) {
        frame = requestAnimationFrame(measure);
      }
    };

    measure();

    // The row height shrinks (fillHeight) or the outer stack grows
    // (non-fillHeight) whenever the page header changes height.
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(row);
    if (row.parentElement) {
      observer.observe(row.parentElement);
    }
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      observer.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [setPageContentTop]);

  return (
    <Stack
      id={PAGE_CONTENT_ROW_ID}
      direction="row"
      useFlexGap
      spacing={1.25}
      sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
    >
      {children}
      <Sidebar />
    </Stack>
  );
}
